import React, { useState, useRef, useCallback, useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Document, Page, pdfjs } from 'react-pdf';
import HtmlChartArtifact from './ChatMessage/components/HtmlChartArtifact';
import EmailEditSheet from './ChatMessage/components/EmailEditSheet';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const normalizeTo = (str) => {
  if (!str || typeof str !== 'string') return '';
  const trimmed = str.trim();
  const match = trimmed.match(/<([^>]+)>/);
  return match ? match[1].trim() : trimmed;
};

const fileNameToTitle = (fileName) => {
  if (!fileName || typeof fileName !== 'string') return 'Chart';
  return fileName
    .replace(/\.[^/.]+$/, '')        // strip extension
    .replace(/[_-]+/g, ' ')          // underscores/hyphens → spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()); // title case
};

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 360;
const MAX_WIDTH = 1400;
const ARTIFACT_PANEL_WIDTH_KEY = 'artifactPanelWidth';

function getStoredWidth() {
  try {
    const w = parseInt(localStorage.getItem(ARTIFACT_PANEL_WIDTH_KEY), 10);
    if (Number.isFinite(w) && w >= MIN_WIDTH && w <= MAX_WIDTH) return w;
  } catch (e) { /* ignore */ }
  return DEFAULT_WIDTH;
}

const getBearerHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isPdfFile = (generatedFile) => {
  const format = generatedFile?.format?.toLowerCase?.() || '';
  const fileName = generatedFile?.fileName?.toLowerCase?.() || '';
  return format === 'pdf' || fileName.endsWith('.pdf');
};

const PdfArtifactView = ({ chatId, messageId, generatedFile, darkMode }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const pageViewportRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let blobUrl = null;

    const loadPdf = async () => {
      if (!chatId || !messageId) {
        if (isMounted) {
          setError('Missing chat or message reference for this PDF.');
          setLoading(false);
        }
        return;
      }

      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const response = await authenticatedFetch(
          `${API_URL}/chat/${chatId}/messages/${messageId}/download-file`,
          {
            headers: {
              ...getBearerHeaders()
            }
          }
        );
        if (!response.ok) throw new Error('Failed to load PDF');
        const blob = await response.blob();
        blobUrl = window.URL.createObjectURL(blob);
        if (isMounted) {
          setPdfUrl(blobUrl);
          setError('');
        }
      } catch {
        if (isMounted) {
          setError('Unable to preview this PDF right now.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      isMounted = false;
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [chatId, messageId]);

  useEffect(() => {
    if (loading || !pdfUrl) return;

    const node = pageViewportRef.current;
    if (!node) return;

    const updateWidth = () => {
      // Keep a small gutter so the page never clips against container borders.
      setPageWidth(Math.max(220, Math.floor(node.clientWidth - 8)));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [loading, pdfUrl]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-sm" style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}>
        Loading PDF preview...
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center text-sm" style={{ color: darkMode ? '#FCA5A5' : '#B91C1C' }}>
        {error || 'Unable to preview this PDF.'}
      </div>
    );
  }

  const totalPages = numPages || 0;
  const downloadFileName = generatedFile?.fileName || 'document.pdf';

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', downloadFileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  return (
    <div className="h-full w-full p-3 flex flex-col gap-3">
      <div
        className="flex items-center justify-between px-3 py-2 rounded-lg border"
        style={{
          borderColor: darkMode ? '#2f2f2f' : '#E5E7EB',
          background: darkMode ? '#171717' : '#F9FAFB'
        }}
      >
        <div className="text-xs" style={{ color: darkMode ? '#D1D5DB' : '#374151' }}>
          {totalPages > 0 ? `${totalPages} page${totalPages === 1 ? '' : 's'}` : 'Loading pages...'}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}>
            Auto fit
          </span>
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="px-2.5 py-1 text-xs rounded-md border"
            style={{ borderColor: darkMode ? '#3b3b3b' : '#D1D5DB' }}
          >
            Download
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 dark:border-[#333333] bg-[#f3f4f6] dark:bg-[#0f0f0f] p-3">
        <div ref={pageViewportRef} className="w-full flex flex-col items-center py-2">
          <Document
            file={pdfUrl}
            loading={
              <div className="text-sm py-8" style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}>
                Rendering PDF...
              </div>
            }
            onLoadSuccess={({ numPages: loadedPages }) => {
              setNumPages(loadedPages || 0);
            }}
            onLoadError={() => setError('Unable to render this PDF.')}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <div key={`pdf-page-${page}`} className="w-full flex justify-center mb-6 last:mb-0">
                <Page
                  pageNumber={page}
                  width={pageWidth || undefined}
                  renderAnnotationLayer
                  renderTextLayer
                />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

/** Extracts the actual code/html from the streaming JSON wrapper {"code":"..."} */
function extractCode(raw) {
  if (!raw || typeof raw !== 'string') return '';
  try {
    const parsed = JSON.parse(raw);
    return parsed.code || parsed.html || raw;
  } catch {
    const codeIdx = raw.indexOf('"code":"');
    if (codeIdx >= 0) return raw.slice(codeIdx + 8).replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\t/g, '\t');
    const htmlIdx = raw.indexOf('"html":"');
    if (htmlIdx >= 0) return raw.slice(htmlIdx + 8).replace(/\\n/g, '\n').replace(/\\"/g, '"');
    return raw;
  }
}

/** Streams code in real-time and transitions to a download card when done */
const CodeStreamingView = ({ codePreview, generatedFile, isComplete, darkMode, chatId, messageId }) => {
  const scrollRef = useRef(null);
  const code = extractCode(codePreview || '');
  const lineCount = code ? code.split('\n').length : 0;

  // Auto-scroll to bottom as code streams in
  useEffect(() => {
    if (!isComplete && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [code, isComplete]);

  const handleDownload = async () => {
    if (!generatedFile) return;
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await authenticatedFetch(`${API_URL}/chat/${chatId}/messages/${messageId}/download`, {
        headers: {
          ...getBearerHeaders()
        }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', generatedFile.fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback to presigned URL
      if (generatedFile.s3Url) window.open(generatedFile.s3Url, '_blank');
    }
  };

  const bg = darkMode ? '#0d0d0d' : '#f8f8f8';
  const codeFg = darkMode ? '#a3b3cc' : '#374151';
  const borderColor = darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const lineNumColor = darkMode ? '#444' : '#ccc';

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Completion banner */}
      {isComplete && generatedFile && (
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4 py-3 mx-4 mt-4 rounded-xl"
          style={{ background: darkMode ? 'rgba(34,197,94,0.10)' : 'rgba(34,197,94,0.08)', border: `1px solid ${darkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}` }}
        >
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" style={{ color: '#22c55e' }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: darkMode ? '#e5e7eb' : '#111' }}>
              {generatedFile.fileName}
            </p>
            <p className="text-xs mt-0.5" style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>
              {(generatedFile.fileSize / 1024).toFixed(0)} KB · Ready to download
            </p>
          </div>
          <button
            onClick={handleDownload}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: darkMode ? '#22c55e' : '#16a34a',
              color: '#fff',
            }}
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            Download
          </button>
        </div>
      )}

      {/* Status bar */}
      {!isComplete && (
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 mx-4 mt-4 rounded-lg"
          style={{ background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', border: `1px solid ${borderColor}` }}
        >
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#3b82f6' }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#3b82f6' }} />
          </span>
          <span className="text-xs font-medium" style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
            Writing file code…
          </span>
          {lineCount > 0 && (
            <span className="ml-auto text-xs tabular-nums" style={{ color: lineNumColor }}>
              {lineCount.toLocaleString()} lines
            </span>
          )}
        </div>
      )}

      {/* Code view */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-auto mx-4 my-3 rounded-xl"
        style={{
          background: bg,
          border: `1px solid ${borderColor}`,
          fontSize: 11.5,
          lineHeight: 1.6,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
        }}
      >
        {code ? (
          <pre
            className="p-4 m-0 whitespace-pre-wrap break-all"
            style={{ color: codeFg, minHeight: '100%' }}
          >
            {code}
          </pre>
        ) : (
          <div className="flex items-center justify-center h-full p-8 text-xs" style={{ color: lineNumColor }}>
            Waiting for code…
          </div>
        )}
      </div>

      {/* Completed code info footer */}
      {isComplete && code && (
        <div className="flex-shrink-0 flex items-center gap-1 px-4 pb-3 text-xs" style={{ color: lineNumColor }}>
          <span>{lineCount.toLocaleString()} lines generated</span>
        </div>
      )}
    </div>
  );
};

const ArtifactPanel = ({ isOpen, onOpen, onClose, artifact, chatId }) => {
  const { darkMode } = useTheme();
  const [panelWidth, setPanelWidth] = useState(getStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isHoveringHandle, setIsHoveringHandle] = useState(false);
  const dragStartX = useRef(null);
  const dragStartWidth = useRef(null);
  const lastWidthRef = useRef(panelWidth);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    lastWidthRef.current = panelWidth;
    setIsDragging(true);
  }, [panelWidth]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const delta = dragStartX.current - e.clientX; // dragging left = wider
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      lastWidthRef.current = newWidth;
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      try {
        const w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, lastWidthRef.current));
        localStorage.setItem(ARTIFACT_PANEL_WIDTH_KEY, String(Math.round(w)));
      } catch (e) { /* ignore */ }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!artifact) return null;

  const { messageId, generatedFile, type } = artifact;
  const isDesignMode = messageId === 'design-mode';
  const isEmailArtifact = type === 'email';
  const isCodeStreaming = type === 'code_streaming';
  const isPdfArtifact = isPdfFile(generatedFile);

  const handleActive = isHoveringHandle || isDragging;

  // Email artifact: find sentAt from sentDrafts
  let emailSentAt = null;
  if (isEmailArtifact && artifact.sentDrafts?.length && artifact.to) {
    const toNorm = normalizeTo(artifact.to);
    const match = artifact.sentDrafts.find(
      (d) => normalizeTo(d.to) === toNorm && (d.subject || '').trim() === (artifact.subject || '').trim()
    );
    if (match) emailSentAt = match.sentAt;
  }

  return (
    <>
      {/* Sliding panel */}
      <aside
        className="artifact-panel"
        style={{
          flex: isOpen ? `0 1 ${panelWidth}px` : '0 0 0',
          width: isOpen ? undefined : 0,
          minWidth: isOpen ? MIN_WIDTH : 0,
          maxWidth: isOpen ? panelWidth : 0,
          overflow: isOpen ? 'visible' : 'hidden',
          transition: isDragging ? 'none' : 'flex 0.3s ease-out, min-width 0.3s ease-out, max-width 0.3s ease-out',
        }}
        aria-hidden={!isOpen}
      >
        {/* Drag handle — sits on the left edge, full height */}
        {isOpen && (
          <div
            className="artifact-resize-handle"
            data-active={handleActive}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setIsHoveringHandle(true)}
            onMouseLeave={() => setIsHoveringHandle(false)}
            aria-hidden="true"
          >
            {/* Pill indicator in the center */}
            <div className="artifact-resize-pill" data-active={handleActive} />
          </div>
        )}

        <div className="artifact-panel-inner h-full flex flex-col min-w-0 bg-white dark:bg-[#111111] border-l border-gray-200 dark:border-[#333333]">
          {/* Header - only for chart/code artifacts; email has its own header */}
          {!isEmailArtifact && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#333333] flex-shrink-0">
              {isCodeStreaming ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {artifact.isComplete && artifact.generatedFile
                      ? fileNameToTitle(artifact.generatedFile.fileName)
                      : 'Generating file…'}
                  </span>
                  {!artifact.isComplete && (
                    <span className="text-xs px-1.5 py-0.5 rounded-md font-medium flex-shrink-0"
                      style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                      Generating
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {fileNameToTitle(generatedFile?.fileName)}
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                aria-label="Close artifact panel"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className={`flex-1 min-h-0 ${!isEmailArtifact && !isCodeStreaming && !isPdfArtifact ? 'overflow-auto px-4' : ''}`}>
            {isEmailArtifact ? (
              <EmailEditSheet
                initialTo={artifact.to || ''}
                initialFrom={artifact.from || ''}
                initialSubject={artifact.subject || ''}
                initialBody={artifact.body || ''}
                emailSource={artifact.emailSource || 'gmail'}
                darkMode={darkMode}
                onClose={onClose}
                onSend={artifact.onSend}
                onSave={artifact.onSave}
                sendLoading={artifact.sendDraftLoading || false}
                sentAt={emailSentAt}
              />
            ) : isCodeStreaming ? (
              <CodeStreamingView
                codePreview={artifact.codePreview}
                generatedFile={artifact.generatedFile}
                isComplete={artifact.isComplete}
                darkMode={darkMode}
                chatId={chatId}
                messageId={messageId}
              />
            ) : isDesignMode ? (
              <div className="p-6 flex flex-col items-center justify-center min-h-[300px] text-gray-500 dark:text-gray-400 text-sm">
                <span>Design mode — artifact panel</span>
                <span className="mt-2 text-xs">Chart preview will appear here</span>
              </div>
            ) : isPdfArtifact ? (
              <PdfArtifactView
                chatId={chatId}
                messageId={messageId}
                generatedFile={generatedFile}
                darkMode={darkMode}
              />
            ) : generatedFile?.format === 'html' && (
              <HtmlChartArtifact
                chatId={chatId}
                messageId={messageId}
                generatedFile={generatedFile}
                darkMode={darkMode}
              />
            )}
          </div>
        </div>
      </aside>

      {/* Full-screen drag cursor overlay while dragging */}
      {isDragging && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, cursor: 'col-resize'
        }} />
      )}
    </>
  );
};

export default ArtifactPanel;
